import { useState, useEffect, useRef } from 'react'
import type { Member } from '@zeni/shared'
import { useLanguage } from '../contexts/LanguageContext'

interface Props {
  /** 選択中のメンバーID。未設定は空文字 */
  value: string
  /** 候補にするメンバー。呼び出し側が取得済みのものを渡す */
  members: Member[]
  onChange: (memberId: string) => void
  placeholder?: string
  className?: string
}

/**
 * 所有者（メンバー）を検索して選ぶコンボボックス。
 * 金融機関（{@link InstitutionCombobox}）と違い自由入力は受け付けず、
 * 入力はあくまで候補の絞り込みに使う。選べるのは候補と「未設定」だけ。
 */
export function MemberCombobox({ value, members, onChange, placeholder, className }: Props) {
  const { t } = useLanguage()
  const selectedName = members.find((m) => m.id === value)?.name ?? ''
  const [query, setQuery] = useState(selectedName)
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // フォームのリセットや初期値の設定に追随させる
  useEffect(() => {
    setQuery(selectedName)
  }, [selectedName])

  // 外側をクリックしたら閉じる。絞り込みの途中でも選択中の名前に戻す
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery(selectedName)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [selectedName])

  const handleSelect = (memberId: string, name: string) => {
    setQuery(name)
    onChange(memberId)
    setOpen(false)
  }

  // 選択中の名前がそのまま入っている状態は「絞り込んでいない」とみなして全件出す
  const filtered = query.length > 0 && query !== selectedName
    ? members.filter((m) => m.name.includes(query))
    : members

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); setQuery(selectedName) } }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto dark:border-gray-600 dark:bg-gray-800">
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); handleSelect('', '') }}
            className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-indigo-50 dark:text-gray-500 dark:hover:bg-indigo-950 transition-colors"
          >
            {t('accounts_form_member_none')}
          </button>
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">{t('member_no_result')}</div>
          )}
          {filtered.map((m) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(m.id, m.name) }}
              className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-indigo-50 dark:text-gray-200 dark:hover:bg-indigo-950 transition-colors"
            >
              {m.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
